import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Ticket, ChevronRight, ChevronLeft, Radar, XCircle, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate, fmtTime, tierFor, statusStyle } from "@/lib/format";

const BookingRow = ({ b, track, onCancel }) => (
  <div className="card-soft flex flex-col gap-2 p-4 hover:border-blue-300 sm:flex-row sm:items-center">
  <Link to={`/ticket/${b.code}`} className="btn-press flex flex-1 items-center gap-4" data-testid={`my-booking-${b.code}`}>
    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-blue-50 text-[#0052FF]"><Ticket className="h-5 w-5" /></span>
    <div className="min-w-0 flex-1">
      <div className="font-display font-bold">{b.trip.origin_city} → {b.trip.destination_city}</div>
      <div className="text-xs text-slate-500">{fmtDate(b.trip.departure)} · {fmtTime(b.trip.departure)} · Seat {b.passengers.map((p) => p.seat).join(", ")} · <span className="font-mono">{b.code}</span></div>
    </div>
    <div className="text-right">
      <div className="font-bold">{money(b.total)}</div>
      <span className={`chip border capitalize ${statusStyle[b.status]}`}>{b.status}</span>
      {b.status === "cancelled" && b.refund_amount > 0 && <div className="mt-1 text-[11px] font-semibold text-amber-600">{b.refund_status === "pending" ? "Refund pending" : "Refunded"} {money(b.refund_amount)}</div>}
    </div>
    <ChevronRight className="hidden h-5 w-5 text-slate-300 sm:block" />
  </Link>
  <div className="flex gap-2">
    {track && <Link to={`/track/${b.code}`} className="chip btn-press justify-center bg-[#0052FF] py-2 text-white hover:bg-[#0041CC]" data-testid={`my-booking-track-${b.code}`}><Radar className="h-4 w-4" />Track bus</Link>}
    {onCancel && <button onClick={() => onCancel(b)} className="chip btn-press justify-center border border-rose-200 bg-white py-2 text-rose-600 hover:bg-rose-50" data-testid={`my-booking-cancel-${b.code}`}><XCircle className="h-4 w-4" />Cancel</button>}
  </div>
  </div>
);

function CancelDialog({ b, onClose, onDone }) {
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/my/bookings/${b.code}/cancel-quote`).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e))); }, [b.code]);
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/my/bookings/${b.code}/cancel`, {}); toast.success("Your ticket has been cancelled"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="customer-cancel-dialog">
        <DialogHeader><DialogTitle className="font-display">Cancel ticket {b.code}</DialogTitle></DialogHeader>
        {err ? <p className="text-sm font-semibold text-rose-600" data-testid="customer-cancel-error">{err}</p>
          : !q ? <div className="grid h-24 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#0052FF]" /></div>
          : (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Cancellation policy: 12h+ before departure 10%, 6-12h 20%, under 6h 50%. No cancellation after the bus departs.</p>
              <div className="card-soft grid grid-cols-3 gap-2 p-3 text-center">
                <div><div className="label-xs">Paid</div><b>{money(q.total)}</b></div>
                <div><div className="label-xs">Charge ({q.percent}%)</div><b className="text-rose-600">{money(q.fee)}</b></div>
                <div><div className="label-xs">Refund</div><b className="text-emerald-700">{money(q.refund)}</b></div>
              </div>
              {q.refund > 0 && <p className="text-xs text-slate-500">Your refund of {money(q.refund)} goes back to your {q.payment_method} and shows as "Refund pending" until processed.</p>}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose} data-testid="customer-cancel-dismiss">Keep ticket</Button>
                <Button disabled={busy} onClick={confirm} className="flex-1 rounded-xl bg-rose-500 hover:bg-rose-600" data-testid="customer-cancel-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel ticket"}</Button>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

export default function AccountPage() {
  const { user, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [cancelFor, setCancelFor] = useState(null);
  const [histPage, setHistPage] = useState(1);
  const load = useCallback(() => api.get("/me/bookings").then((r) => setList(r.data)), []);
  useEffect(() => { load(); refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const now = new Date().toISOString().slice(0, 16);
  const upcoming = list.filter((b) => b.trip.departure >= now && ["confirmed", "reserved"].includes(b.status)).reverse();
  const past = list.filter((b) => !upcoming.includes(b));
  const miles = user?.miles || 0;
  const tier = tierFor(miles);
  const pct = tier.next ? Math.min(100, ((miles - tier.min) / (tier.next.min - tier.min)) * 100) : 100;

  return (
    <div className="hero-bg min-h-full">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Akwaaba, {user?.name.split(" ")[0]}</h1>
        <div className="rise overflow-hidden rounded-3xl bg-gradient-to-br from-[#0052FF] via-[#1D6BFF] to-[#38BDF8] p-6 text-white shadow-[0_24px_48px_-24px_rgba(0,82,255,0.8)]" data-testid="loyalty-card">
          <div className="flex items-start justify-between">
            <div><div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-100">GPRTU Miles</div>
              <div className="mt-1 font-display text-5xl font-extrabold" data-testid="loyalty-miles">{miles.toLocaleString()}</div></div>
            <span className={`chip ${tier.color}`} data-testid="loyalty-tier"><Award className="h-3.5 w-3.5" />{tier.name}</span>
          </div>
          <Progress value={pct} className="mt-5 h-2 bg-white/30 [&>div]:bg-yellow-300" />
          <div className="mt-2 text-sm text-blue-50">{tier.next ? `${(tier.next.min - miles).toLocaleString()} miles to ${tier.next.name}` : "Top tier reached - thank you!"} · Earn 1 mile per GH₵ 1 spent</div>
        </div>
        <section>
          <h2 className="mb-3 font-display text-lg font-bold">Upcoming trips</h2>
          <div className="space-y-2" data-testid="upcoming-trips">
            {upcoming.length ? upcoming.map((b) => <BookingRow key={b.id} b={b} track onCancel={setCancelFor} />) : <p className="card-soft p-5 text-sm text-slate-500">No upcoming trips. <Link to="/" className="font-semibold text-[#0052FF]">Book one now</Link></p>}
          </div>
        </section>
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Ticket history</h2>
            {past.length > 5 && <span className="text-xs text-slate-500" data-testid="history-count">{past.length} tickets</span>}
          </div>
          <div className="space-y-2" data-testid="past-trips">
            {past.length ? past.slice((histPage - 1) * 5, histPage * 5).map((b) => <BookingRow key={b.id} b={b} />) : <p className="card-soft p-5 text-sm text-slate-500">Your past tickets will appear here.</p>}
          </div>
          {past.length > 5 && (
            <div className="mt-4 flex items-center justify-center gap-3" data-testid="history-pager">
              <Button variant="outline" size="sm" className="rounded-full" disabled={histPage === 1} onClick={() => setHistPage((p) => p - 1)} data-testid="history-prev"><ChevronLeft className="h-4 w-4" />Prev</Button>
              <span className="text-sm font-semibold text-slate-600" data-testid="history-page">Page {histPage} of {Math.ceil(past.length / 5)}</span>
              <Button variant="outline" size="sm" className="rounded-full" disabled={histPage >= Math.ceil(past.length / 5)} onClick={() => setHistPage((p) => p + 1)} data-testid="history-next">Next<ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </section>
      </div>
      {cancelFor && <CancelDialog b={cancelFor} onClose={() => setCancelFor(null)} onDone={load} />}
    </div>
  );
}
